import { useMemo, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import type {
  ProcedureBlockItem,
  ProcedureCompactVariant,
  StepImageBinding,
} from "../../utils/ragChatUtils";
import {
  cleanStepDisplayText,
  imagesForCitedText,
  isBareStepPhaseHeading,
  isStandaloneCautionSectionTitle,
  sectionCitedImages,
} from "../../utils/ragChatUtils";
import type { RagImageItem } from "../../types/ragChatTypes";
import { RagMarkdownContent } from "./RagMarkdownContent";
import { StepImageCollage } from "./StepImageCollage";

/** Where cited images sit relative to the section text. */
export type CompactImagePlacement = "below" | "beside";

export interface ProcedureCompactSectionProps {
  title: string;
  variant: ProcedureCompactVariant;
  body: string;
  items: ProcedureBlockItem[];
  /** Page→images map; when set, cite `[p.N]` on body/items to show collages. */
  inlineImages?: Record<string, RagImageItem[]>;
  onOpenImage?: (src: string, gallery: StepImageBinding[]) => void;
  tapToOpenLabel?: string;
  morePhotosLabel?: (count: number) => string;
  zoomLabel?: string;
  /**
   * `beside` — text left, section collage right (post-catalog Removing/Installing).
   * `below` — collage under text/items (Notes and default).
   */
  imagePlacement?: CompactImagePlacement;
}

const COUNT_PILL_SX = {
  ml: 0.5,
  px: 1,
  py: 0.25,
  borderRadius: 10,
  bgcolor: "grey.200",
  color: "text.secondary",
  fontSize: 12,
  fontWeight: 700,
  minWidth: 22,
  textAlign: "center" as const,
};

const TOOL_NUMBER_PILL_SX = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  width: 22,
  height: 22,
  borderRadius: "50%",
  bgcolor: "#0084F4",
  color: "common.white",
  fontSize: 11,
  fontWeight: 700,
  lineHeight: 1,
} as const;

function variantIcon(variant: ProcedureCompactVariant) {
  switch (variant) {
    case "tools":
      return <BuildOutlinedIcon sx={{ color: "primary.main", fontSize: 18 }} />;
    case "safety":
      return <ShieldOutlinedIcon sx={{ color: "primary.main", fontSize: 18 }} />;
    case "documents":
      return <ArticleOutlinedIcon sx={{ color: "primary.main", fontSize: 18 }} />;
    default:
      return <InfoOutlinedIcon sx={{ color: "primary.main", fontSize: 18 }} />;
  }
}

function variantHeading(
  variant: ProcedureCompactVariant,
  title: string,
  t: (key: string) => string
): string {
  if (variant === "tools") return t("opva.chatAssistanceToolsRequired");
  // Keep source titles like "Do Not"; only rename classic Safety Precautions.
  if (variant === "safety" && !isStandaloneCautionSectionTitle(title)) {
    return t("opva.chatAssistanceSafetyNotices");
  }
  return title;
}

function ToolsItemList({ items }: { items: ProcedureBlockItem[] }) {
  return (
    <Box component="ol" sx={{ m: 0, p: 0, listStyle: "none" }}>
      {items.map((item, i) => {
        const n = item.num || String(i + 1);
        return (
          <Box
            component="li"
            key={`tool-${n}-${i}`}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              mb: i === items.length - 1 ? 0 : 1,
            }}
          >
            <Box component="span" sx={TOOL_NUMBER_PILL_SX} aria-hidden>
              {n}
            </Box>
            <Box
              sx={{
                typography: "bodySmall",
                color: "text.secondary",
                lineHeight: 1.5,
                pt: "2px",
                minWidth: 0,
                "& p": { m: 0 },
              }}
            >
              <RagMarkdownContent text={item.text} dense />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

function splitStepPhaseItem(text: string): { heading: string; body: string } {
  const raw = String(text ?? "").trim();
  const nl = raw.indexOf("\n");
  if (nl < 0) return { heading: raw, body: "" };
  return {
    heading: raw.slice(0, nl).trim(),
    body: raw.slice(nl + 1).trim(),
  };
}

function NestedStepPhaseList({ items }: { items: ProcedureBlockItem[] }) {
  return (
    <Box>
      {items.map((item, i) => {
        const { heading, body } = splitStepPhaseItem(item.text);
        return (
          <Box
            key={`phase-${i}`}
            data-testid="compact-step-phase"
            sx={{ mb: i === items.length - 1 ? 0 : 1.5 }}
          >
            <Typography
              component="h4"
              sx={{
                fontSize: 14,
                fontWeight: 700,
                lineHeight: 1.4,
                color: "text.primary",
                mb: body ? 0.5 : 0,
              }}
            >
              {heading}
            </Typography>
            {body ? (
              <Box
                sx={{
                  typography: "bodySmall",
                  color: "text.secondary",
                  lineHeight: 1.55,
                  "& p": { m: 0, mb: 0.5 },
                  "& p:last-child": { mb: 0 },
                  "& ul, & ol": { m: 0, pl: 2.25 },
                  "& li": { mb: 0.25 },
                }}
              >
                <RagMarkdownContent text={body} dense />
              </Box>
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
}

function CompactCitedCollage({
  images,
  onOpenImage,
  tapToOpenLabel,
  morePhotosLabel,
  zoomLabel,
  sx,
}: {
  images: StepImageBinding[];
  onOpenImage?: (src: string, gallery: StepImageBinding[]) => void;
  tapToOpenLabel: string;
  morePhotosLabel: (count: number) => string;
  zoomLabel: string;
  sx?: Record<string, unknown>;
}) {
  if (!images.length || !onOpenImage) return null;
  return (
    <Box sx={{ mt: 1, ...sx }} data-testid="compact-cited-images">
      <StepImageCollage
        items={images}
        onOpen={(src) => onOpenImage(src, images)}
        tapToOpenLabel={tapToOpenLabel}
        morePhotosLabel={morePhotosLabel}
        zoomLabel={zoomLabel}
      />
    </Box>
  );
}

function CompactBody({
  body,
  items,
  numberedTools,
  inlineImages,
  onOpenImage,
  tapToOpenLabel,
  morePhotosLabel,
  zoomLabel,
  imagePlacement,
}: {
  body: string;
  items: ProcedureBlockItem[];
  numberedTools: boolean;
  inlineImages?: Record<string, RagImageItem[]>;
  onOpenImage?: (src: string, gallery: StepImageBinding[]) => void;
  tapToOpenLabel: string;
  morePhotosLabel: (count: number) => string;
  zoomLabel: string;
  imagePlacement: CompactImagePlacement;
}) {
  const bodyImages = useMemo(
    () =>
      inlineImages && !numberedTools
        ? imagesForCitedText(body, inlineImages)
        : [],
    [body, inlineImages, numberedTools]
  );
  const itemImages = useMemo(() => {
    if (!inlineImages || numberedTools) return [] as StepImageBinding[][];
    return items.map((item) => imagesForCitedText(item.text, inlineImages));
  }, [items, inlineImages, numberedTools]);
  const besideImages = useMemo(() => {
    if (!inlineImages || numberedTools || imagePlacement !== "beside") {
      return [] as StepImageBinding[];
    }
    return sectionCitedImages(body, items, inlineImages);
  }, [body, items, inlineImages, numberedTools, imagePlacement]);

  // Prefer per-item collages when list items cite pages; otherwise body-level.
  const showBodyImages =
    imagePlacement === "below" &&
    bodyImages.length > 0 &&
    itemImages.every((imgs) => imgs.length === 0);
  const showBeside = imagePlacement === "beside" && besideImages.length > 0;

  const textColumn = (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      {body ? (
        <Box
          sx={{
            typography: "bodySmall",
            color: "text.secondary",
            lineHeight: 1.55,
            mb: items.length || (showBodyImages && !showBeside) ? 1 : 0,
            "& p": { m: 0, mb: 0.75 },
            "& p:last-child": { mb: 0 },
          }}
        >
          <RagMarkdownContent text={cleanStepDisplayText(body)} dense />
        </Box>
      ) : null}
      {showBodyImages ? (
        <CompactCitedCollage
          images={bodyImages}
          onOpenImage={onOpenImage}
          tapToOpenLabel={tapToOpenLabel}
          morePhotosLabel={morePhotosLabel}
          zoomLabel={zoomLabel}
        />
      ) : null}

      {items.length > 0 &&
        (numberedTools ? (
          <ToolsItemList items={items} />
        ) : items.every((it) => isBareStepPhaseHeading(it.text)) ? (
          <NestedStepPhaseList items={items} />
        ) : (
          <Box
            component="ul"
            sx={{
              pl: 2,
              my: 0,
              listStyleType: "disc",
              "& li": { display: "list-item", mb: 0.75 },
            }}
          >
            {items.map((item, i) => (
              <Box
                component="li"
                key={`${item.num ?? "b"}-${i}`}
                sx={{
                  typography: "bodySmall",
                  lineHeight: 1.5,
                  color: "text.secondary",
                }}
              >
                <RagMarkdownContent
                  text={cleanStepDisplayText(item.text)}
                  dense
                />
                {imagePlacement === "below" ? (
                  <CompactCitedCollage
                    images={itemImages[i] ?? []}
                    onOpenImage={onOpenImage}
                    tapToOpenLabel={tapToOpenLabel}
                    morePhotosLabel={morePhotosLabel}
                    zoomLabel={zoomLabel}
                  />
                ) : null}
              </Box>
            ))}
          </Box>
        ))}
    </Box>
  );

  if (showBeside) {
    return (
      <Box
        sx={{
          px: 1.5,
          py: 1.25,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "flex-start" },
          gap: 1.5,
        }}
        data-testid="compact-body-beside"
      >
        {textColumn}
        <Box
          sx={{
            flex: "0 0 auto",
            width: { xs: "100%", sm: 168 },
            maxWidth: { xs: "100%", sm: 168 },
            mt: 0,
          }}
        >
          <CompactCitedCollage
            images={besideImages}
            onOpenImage={onOpenImage}
            tapToOpenLabel={tapToOpenLabel}
            morePhotosLabel={morePhotosLabel}
            zoomLabel={zoomLabel}
            sx={{ mt: 0 }}
          />
        </Box>
      </Box>
    );
  }

  return <Box sx={{ px: 1.5, py: 1.25 }}>{textColumn}</Box>;
}

/**
 * Compact informational procedure section — matches the collapsible rail card
 * chrome used by Safety notices / tables (icon + title + optional count).
 * Tools variant is a clickable accordion (collapsed by default).
 */
export function ProcedureCompactSection({
  title,
  variant,
  body,
  items,
  inlineImages,
  onOpenImage,
  tapToOpenLabel,
  morePhotosLabel,
  zoomLabel,
  imagePlacement = "below",
}: ProcedureCompactSectionProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const heading = variantHeading(variant, title, t);
  const count = items.length > 0 ? items.length : undefined;
  const isToolsAccordion = variant === "tools";
  const collageTap = tapToOpenLabel ?? t("opva.chatAssistanceTapToOpen");
  const collageMore =
    morePhotosLabel ??
    ((n: number) => t("opva.chatAssistanceMorePhotos", { count: n }));
  const collageZoom = zoomLabel ?? t("opva.chatAssistanceClickToEnlarge");

  const cardSx = {
    borderRadius: 2,
    border: "1px solid",
    borderColor: "divider",
    bgcolor: "background.paper",
    overflow: "hidden",
    boxShadow: "0 2px 8px rgba(28,46,54,0.05)",
  } as const;

  const bodyProps = {
    body,
    items,
    inlineImages,
    onOpenImage,
    tapToOpenLabel: collageTap,
    morePhotosLabel: collageMore,
    zoomLabel: collageZoom,
    imagePlacement,
  };

  if (isToolsAccordion) {
    return (
      <Box sx={cardSx} data-testid="procedure-tools-accordion">
        <Box sx={{ px: 1.5, py: 1, bgcolor: "grey.50" }}>
          <Button
            fullWidth
            size="small"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="procedure-tools-panel"
            id="procedure-tools-toggle"
            disableRipple
            sx={{
              textTransform: "none",
              color: "text.secondary",
              px: 0,
              py: 0.25,
              minWidth: 0,
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, gap: 0.75 }}>
              {variantIcon(variant)}
              <Typography variant="labelSmall" fontWeight={700} noWrap>
                {heading}
              </Typography>
              {typeof count === "number" && (
                <Box component="span" sx={COUNT_PILL_SX}>
                  {count}
                </Box>
              )}
            </Box>
            {open ? (
              <ExpandMoreIcon sx={{ fontSize: 20, color: "text.disabled", flexShrink: 0 }} />
            ) : (
              <ChevronRightIcon sx={{ fontSize: 20, color: "text.disabled", flexShrink: 0 }} />
            )}
          </Button>
        </Box>

        {open ? (
          <Box
            id="procedure-tools-panel"
            role="region"
            aria-labelledby="procedure-tools-toggle"
            sx={{ bgcolor: "grey.50", borderTop: "1px solid", borderColor: "divider" }}
          >
            <CompactBody {...bodyProps} numberedTools />
          </Box>
        ) : null}
      </Box>
    );
  }

  return (
    <Box sx={cardSx} data-testid="procedure-compact-section" data-variant={variant}>
      <Box sx={{ px: 1.5, py: 1, bgcolor: "grey.50" }}>
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, gap: 0.75 }}>
          {variantIcon(variant)}
          <Typography variant="labelSmall" fontWeight={700} noWrap>
            {heading}
          </Typography>
          {typeof count === "number" && (
            <Box component="span" sx={COUNT_PILL_SX}>
              {count}
            </Box>
          )}
        </Box>
      </Box>

      <CompactBody {...bodyProps} numberedTools={false} />
    </Box>
  );
}
